import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, useNavigationType, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { Bell, Wallet, User, ChevronDown, QrCode } from 'lucide-react';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import Toast from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import AutoUpdater from './components/AutoUpdater';
import NetworkStatus from './components/NetworkStatus';
import { SkeletonAppShell, SkeletonNotificationsList } from './components/Skeleton';
import './components/Skeleton.css'; // preload skeleton styles before Suspense fallback
import * as analytics from './utils/analytics';
import { isStandaloneMode } from './pwa/installDetection';
import { prefetchCoreRoutes } from './utils/prefetch';
import { CACHE_KEYS, readCache, writeCache } from './utils/dataCache';
import { consumeEarlyWallet, consumeEarlyNotifications } from './services/earlyFetch';
import { postVisitPing } from './services/api';
import { isIOS } from './utils/iosLayout';
import { useBottomNavHidden } from './utils/bottomNavVisibility';
import './App.css';

// Route-level code splitting: load components only when their route is visited
const activityListImport = () => import('./components/ActivityList');
const landingPageImport = () => import('./components/LandingPage');
const NotificationOverlay = lazy(() => import('./components/NotificationOverlay'));
const ActivityList = lazy(activityListImport);
const ActivityDetail = lazy(() => import('./components/ActivityDetail'));
const CreateActivity = lazy(() => import('./components/CreateActivity'));
const PaymentCollection = lazy(() => import('./components/PaymentCollection'));
const ExpenseTracking = lazy(() => import('./components/ExpenseTracking'));
const BalanceCalculation = lazy(() => import('./components/BalanceCalculation'));
const Login = lazy(() => import('./components/Login'));
const AttendeeSelection = lazy(() => import('./components/AttendeeSelection'));
const Profile = lazy(() => import('./components/Profile'));
const Groups = lazy(() => import('./components/Groups'));
const Balance = lazy(() => import('./components/Balance'));
const BottomNav = lazy(() => import('./components/BottomNav'));
const InviteParticipants = lazy(() => import('./components/InviteParticipants'));
const JoinGroup = lazy(() => import('./components/JoinGroup'));
const JoinActivity = lazy(() => import('./components/JoinActivity'));
const EditActivity = lazy(() => import('./components/EditActivity'));
const PricingWizard = lazy(() => import('./components/PricingWizard'));
const LandingPage = lazy(landingPageImport);
const SetPaymentCollection = lazy(() => import('./components/SetPaymentCollection'));
const PaySmart = lazy(() => import('./components/PaySmart'));
const SharePaymentScreenshot = lazy(() => import('./components/SharePaymentScreenshot'));
const OrganizerDashboard = lazy(() => import('./components/OrganizerDashboard'));
const BudgetPlanning = lazy(() => import('./components/BudgetPlanning'));
const ScheduleManage = lazy(() => import('./components/ScheduleManage'));
const LetsCelebrate = lazy(() => import('./components/LetsCelebrate'));
const DirectPaymentEntry = lazy(() => import('./components/DirectPaymentEntry'));
const PostPayGroupProfile = lazy(() => import('./components/PostPayGroupProfile'));
const Terms = lazy(() => import('./components/Terms'));
const Privacy = lazy(() => import('./components/Privacy'));
const DeleteAccount = lazy(() => import('./components/DeleteAccount'));
const PublicInfoPage = lazy(() => import('./components/PublicInfoPage'));
const Offers = lazy(() => import('./components/Offers'));
const AdminOffers = lazy(() => import('./components/AdminOffers'));
const AdminPayments = lazy(() => import('./components/AdminPayments'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminFeatures = lazy(() => import('./components/AdminFeatures'));
const ContinueSetup = lazy(() => import('./components/ContinueSetup'));
const GroupProfileFieldsConfig = lazy(() => import('./components/GroupProfileFieldsConfig'));
const GroupTreasurerSetup = lazy(() => import('./components/GroupTreasurerSetup'));
const ProRequestApproval = lazy(() => import('./components/ProRequestApproval'));
const QRScanner = lazy(() => import('./components/QRScanner'));
const ManageQRCodes = lazy(() => import('./components/ManageQRCodes'));
const CustomizeGroup = lazy(() => import('./components/CustomizeGroup'));
const GroupSettings = lazy(() => import('./components/GroupSettings'));
const GroupDashboard = lazy(() => import('./components/GroupDashboard'));
const GroupCollections = lazy(() => import('./components/GroupCollections'));
const MyPayments = lazy(() => import('./components/MyPayments'));

// Warm the landing chunk in parallel with React boot (token → home, else → marketing).
try {
  if (typeof window !== 'undefined' && localStorage.getItem('token')) {
    activityListImport();
  } else {
    landingPageImport();
  }
} catch (_) {}

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuth();

  // Seed from device cache so reopen shows last-known balance/badge instantly.
  const cachedWallet = typeof window !== 'undefined' ? readCache(CACHE_KEYS.WALLET) : null;
  const cachedNotif = typeof window !== 'undefined' ? readCache(CACHE_KEYS.NOTIF) : null;
  const hasCachedBalance = cachedWallet != null && typeof cachedWallet.total_balance === 'number';

  const [totalBalance, setTotalBalance] = useState(() =>
    hasCachedBalance ? cachedWallet.total_balance : 0
  );
  // If we have a cached balance, show it immediately (dimmed "updating") instead of "...".
  const [balanceLoading, setBalanceLoading] = useState(!hasCachedBalance);
  const [balanceRefreshing, setBalanceRefreshing] = useState(hasCachedBalance);
  const [notifUnread, setNotifUnread] = useState(() =>
    typeof cachedNotif?.unread === 'number' ? cachedNotif.unread : 0
  );
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  // Skip server re-fetch briefly after local mark-as-read so navigation
  // does not overwrite the badge with a stale unread count.
  const skipNotifFetchUntilRef = useRef(0);

  const handleNotifUnreadChange = useCallback((count) => {
    setNotifUnread(count);
    writeCache(CACHE_KEYS.NOTIF, { unread: count });
    skipNotifFetchUntilRef.current = Date.now() + 5000;
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTotalBalance();
    }
  }, [isAuthenticated]);

  // Prefetch core routes in background for faster navigation
  useEffect(() => {
    if (isAuthenticated) {
      prefetchCoreRoutes();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (Date.now() < skipNotifFetchUntilRef.current) return;
    const fetchNotif = async () => {
      try {
        // Prefer early bootstrap payload when available (one-shot).
        const early = await consumeEarlyNotifications();
        if (early && typeof early.unread === 'number') {
          if (Date.now() < skipNotifFetchUntilRef.current) return;
          setNotifUnread(early.unread);
          writeCache(CACHE_KEYS.NOTIF, { unread: early.unread });
          return;
        }
        const { getUnreadNotificationCount } = await import('./services/api');
        const res = await getUnreadNotificationCount();
        if (Date.now() < skipNotifFetchUntilRef.current) return;
        const unread = res.data?.unread ?? 0;
        setNotifUnread(unread);
        writeCache(CACHE_KEYS.NOTIF, { unread });
      } catch {
        if (Date.now() < skipNotifFetchUntilRef.current) return;
        // Keep cached badge on failure rather than flashing to 0.
      }
    };
    fetchNotif();
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
      // Mobile notification sheet is portaled to document.body, so it is not
      // inside notificationsRef. Treat the sheet itself as "inside".
      const target = e.target;
      const inNotifWrap = notificationsRef.current?.contains(target);
      const inNotifSheet =
        typeof target?.closest === 'function' && !!target.closest('.notification-overlay');
      if (notificationsOpen && !inNotifWrap && !inNotifSheet) {
        setNotificationsOpen(false);
      }
    };
    if (profileDropdownOpen || notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen, notificationsOpen]);

  const handleLogout = () => {
    setProfileDropdownOpen(false);
    logout();
    navigate('/');
  };

  const fetchTotalBalance = async () => {
    try {
      setBalanceRefreshing(true);
      // Prefer early bootstrap wallet payload when available (one-shot).
      const early = await consumeEarlyWallet();
      if (early && typeof early.total_balance === 'number') {
        setTotalBalance(early.total_balance || 0);
        writeCache(CACHE_KEYS.WALLET, {
          total_balance: early.total_balance || 0,
          group_balances: early.group_balances || [],
        });
        return;
      }
      const { getGroupBalances } = await import('./services/api');
      const response = await getGroupBalances();
      const total = response.data.total_balance || 0;
      setTotalBalance(total);
      writeCache(CACHE_KEYS.WALLET, {
        total_balance: total,
        group_balances: response.data.group_balances || [],
      });
    } catch (err) {
      console.error('Failed to fetch total balance:', err);
      // Keep cached balance on failure; only zero out if we never had one.
      if (!hasCachedBalance) setTotalBalance(0);
    } finally {
      setBalanceLoading(false);
      setBalanceRefreshing(false);
    }
  };

  const handleBrandClick = (e) => {
    e.preventDefault();
    navigate('/');
  };

  const apiUrl = (process.env.REACT_APP_API_URL || '').toLowerCase();
  const publicUrl = (process.env.REACT_APP_PUBLIC_URL || '').toLowerCase();
  const isDevApp =
    apiUrl.includes('dev') ||
    publicUrl.includes('dev') ||
    (typeof window !== 'undefined' && window.location.hostname.includes('dev'));
  const appBrandName = isDevApp ? 'choruss-dev' : 'Choruss';
  const accountDisplayName = user?.name?.trim() || 'Profile';

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="navbar-left">
          <Link
            to="/"
            className="navbar-brand"
            onClick={handleBrandClick}
          >
            <img src="/logos/community_knot-sm.png" alt="Choruss Logo" className="navbar-logo" />
            {appBrandName}
          </Link>
          {isAuthenticated && (
            <div className="navbar-links">
              <Link to="/" className="nav-link">Upcoming Events</Link>
              <Link to="/groups" className="nav-link">Groups</Link>
            </div>
          )}
        </div>
        <div className="navbar-right">
          {isAuthenticated ? (
            <div className="account-status">
              <Link to="/scan" className="navbar-scan-btn" aria-label="Scan & Pay" title="Scan & Pay">
                <QrCode size={20} strokeWidth={2} />
                <span className="scan-label">Scan</span>
              </Link>
              <div className="navbar-notifications-wrap" ref={notificationsRef}>
                <button
                  type="button"
                  className="navbar-notifications"
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                  aria-haspopup="dialog"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    setNotificationsOpen((open) => !open);
                  }}
                >
                  <Bell size={20} strokeWidth={2} />
                  {notifUnread > 0 && (
                    <span className="navbar-notifications-badge">{notifUnread > 99 ? '99+' : notifUnread}</span>
                  )}
                </button>
                {notificationsOpen && (
                  <Suspense fallback={<div className="notification-overlay"><SkeletonNotificationsList /></div>}>
                    <NotificationOverlay
                      onClose={() => setNotificationsOpen(false)}
                      onUnreadChange={handleNotifUnreadChange}
                    />
                  </Suspense>
                )}
              </div>
              <Link to="/balance" className="wallet-balance">
                <Wallet size={18} strokeWidth={2} />
                <span className="wallet-info">
                  <span className="wallet-label">Balance</span>
                  <span
                    className={`wallet-amount${balanceRefreshing ? ' wallet-amount-updating' : ''}`}
                    title={balanceRefreshing ? 'Updating…' : undefined}
                  >
                    {balanceLoading ? '...' : `₹${totalBalance.toFixed(2)}`}
                  </span>
                </span>
              </Link>
              <div className="navbar-profile-desktop" ref={profileDropdownRef}>
                <button
                  type="button"
                  className="account-menu-button"
                  onClick={() => {
                    setNotificationsOpen(false);
                    setProfileDropdownOpen((open) => !open);
                  }}
                  aria-expanded={profileDropdownOpen}
                  aria-haspopup="true"
                >
                  <User size={18} strokeWidth={2} />
                  <span className="account-name">{accountDisplayName}</span>
                  <ChevronDown size={14} strokeWidth={2} className={`dropdown-chevron ${profileDropdownOpen ? 'open' : ''}`} />
                </button>
                {profileDropdownOpen && (
                  <div className="account-dropdown">
                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      Edit profile
                    </Link>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Link to="/login" className="btn-login">Login / Sign Up</Link>
          )}
        </div>
      </div>
    </nav>
  );
}

function ChatRouteRedirect() {
  const { id } = useParams();
  return <Navigate to={`/activity/${id}`} replace />;
}

/** SPA fallback when Hosting serves index.html for /api/share/* instead of the backend OG page. */
function ShareActivityRedirect() {
  const { activityId } = useParams();
  return <Navigate to={`/pay/${activityId}`} replace />;
}

function ShareGroupRedirect() {
  const { groupId } = useParams();
  return <Navigate to={`/join-group/${groupId}`} replace />;
}

/** On iPhone the page itself scrolls, so a new screen opens at the top and Back returns to the old spot. */
function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (!isIOS()) return undefined;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (!isIOS()) return undefined;
    const key = `choruss-scroll:${location.key}`;

    if (navType === 'POP') {
      let saved = 0;
      try {
        saved = parseInt(sessionStorage.getItem(key) || '0', 10) || 0;
      } catch (err) {
        saved = 0;
      }
      let tries = 0;
      let timer = 0;
      const restore = () => {
        window.scrollTo(0, saved);
        tries += 1;
        if (tries < 8) timer = window.setTimeout(restore, 120);
      };
      restore();
      return () => window.clearTimeout(timer);
    }

    window.scrollTo(0, 0);
    const remember = () => {
      try {
        sessionStorage.setItem(key, String(window.scrollY || 0));
      } catch (err) {
        // Private mode can block storage. The page still scrolls.
      }
    };
    window.addEventListener('scroll', remember, { passive: true });
    return () => window.removeEventListener('scroll', remember);
  }, [location.key, navType]);

  return null;
}

function AppContent() {
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const location = useLocation();
  const sheetHidesNav = useBottomNavHidden();
  const isRoot = location.pathname === '/';
  const isWelcomeRoute = location.pathname === '/welcome';
  const isAuthRoute = [
    '/login',
    '/terms',
    '/privacy',
    '/delete-account',
    '/about',
    '/how-it-works',
    '/faq',
    '/collect-group-contributions',
    '/track-event-expenses',
    '/split-event-surplus',
  ].includes(location.pathname);
  const isGroupInviteRoute = /^\/join-group\/[^/]+$/.test(location.pathname);
  // Landing at / when logged out; /welcome always shows marketing page (even when logged in on dev).
  const showLandingOnly =
    isWelcomeRoute ||
    (isRoot && !isAuthenticated && !isAuthRoute && !(authLoading && token));
  const showAuthLoading = authLoading && !!token && !isAuthRoute && !isWelcomeRoute && !isGroupInviteRoute;
  const isChatFullscreenRoute =
    location.pathname === '/create-activity' ||
    location.pathname.endsWith('/continue-setup');
  const isCreateActivityRoute = location.pathname === '/create-activity';
  const isPaymentRoute = /^\/activity\/[^/]+\/payment$/.test(location.pathname);
  const isPaySmartRoute = /^\/activity\/[^/]+\/pay-smart$/.test(location.pathname);
  const isPricingRoute = /^\/activity\/[^/]+\/pricing$/.test(location.pathname);
  const isInviteLanding = /^\/pay\/[^/]+$/.test(location.pathname);
  const hideNavOnIos = isIOS() && (
    isPaymentRoute || isPaySmartRoute || isPricingRoute || isInviteLanding || sheetHidesNav
  );
  const shouldHideNav = isCreateActivityRoute || isGroupInviteRoute || hideNavOnIos;
  const showBottomNav = isAuthenticated && !shouldHideNav;

  // Swipe navigation between core bottom-nav tabs (native touch — no framer-motion)
  const navigate = useNavigate();
  const touchStartRef = useRef(null);

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const offsetX = touch.clientX - start.x;
    const offsetY = touch.clientY - start.y;
    // Ignore mostly vertical scrolling
    if (Math.abs(offsetY) > 40) return;

    const threshold = 60;
    const isSwipeLeft = offsetX < -threshold;
    const isSwipeRight = offsetX > threshold;

    const coreRoutes = ['/', '/groups', '/offers', '/profile'];
    const currentIndex = coreRoutes.indexOf(location.pathname);

    if (currentIndex === -1) return;

    if (isSwipeLeft && currentIndex < coreRoutes.length - 1) {
      navigate(coreRoutes[currentIndex + 1]);
    } else if (isSwipeRight && currentIndex > 0) {
      navigate(coreRoutes[currentIndex - 1]);
    }
  };

  useEffect(() => {
    // Chunk already warmed at module load; keep a no-op-safe call for late mounts.
    activityListImport();
  }, []);

  // Remove splash screen when auth loading completes
  useEffect(() => {
    if (!authLoading && typeof window.__removeSplash === 'function') {
      window.__removeSplash();
    }
  }, [authLoading]);

  useEffect(() => {
    analytics.pageView(location.pathname, document.title);
  }, [location.pathname]);

  // First-party visit counter: once per browser session per UTC day
  useEffect(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const key = 'choruss_visit_ping';
      if (sessionStorage.getItem(key) === today) return;
      sessionStorage.setItem(key, today);
      postVisitPing().catch(() => {});
    } catch {
      // ignore storage / network failures
    }
  }, []);

  useEffect(() => {
    if (isStandaloneMode()) {
      analytics.event('pwa_launch_standalone', {});
    }
  }, []);

  return (
    <div className={`App${showBottomNav ? '' : ' app--no-bottom-nav'}`}>
      <ScrollManager />
      <NetworkStatus />
      {showAuthLoading ? (
        <>
          <Navbar />
          {!shouldHideNav && <PWAInstallPrompt />}
          <div className="main-content">
            <div className="container">
              <SkeletonAppShell />
            </div>
          </div>
        </>
      ) : showLandingOnly ? (
        <>
          {!shouldHideNav && <PWAInstallPrompt />}
          <ErrorBoundary>
            <Suspense fallback={<SkeletonAppShell />}>
              <LandingPage />
            </Suspense>
          </ErrorBoundary>
        </>
      ) : (
        <>
          {!shouldHideNav && <Navbar />}
          {!shouldHideNav && <PWAInstallPrompt />}
          <div className={`main-content${isChatFullscreenRoute ? ' main-content--chat-fullscreen' : ''}${isGroupInviteRoute ? ' main-content--invite-only' : ''}`}>
              <div
                key={location.pathname}
                className={`route-page-transition ${isGroupInviteRoute ? 'invite-route-shell' : 'container'}${isChatFullscreenRoute ? ' container--chat-fullscreen' : ''}`}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <ErrorBoundary>
                  <Suspense fallback={<SkeletonAppShell />}>
                    <Routes location={location} key={location.pathname}>
                    <Route path="/welcome" element={<LandingPage />} />
                    <Route path="/" element={<ActivityList />} />
                    <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/delete-account" element={<DeleteAccount />} />
                    <Route path="/about" element={<PublicInfoPage />} />
                    <Route path="/how-it-works" element={<PublicInfoPage />} />
                    <Route path="/faq" element={<PublicInfoPage />} />
                    <Route path="/collect-group-contributions" element={<PublicInfoPage />} />
                    <Route path="/track-event-expenses" element={<PublicInfoPage />} />
                    <Route path="/split-event-surplus" element={<PublicInfoPage />} />
                    <Route path="/create-activity" element={<CreateActivity />} />
                    <Route path="/activity/:id/dashboard" element={<OrganizerDashboard />} />
                    <Route path="/activity/:id/budget-planning" element={<BudgetPlanning />} />
                    <Route path="/activity/:id/schedule/manage" element={<ScheduleManage />} />
                    <Route path="/pay/:activityId" element={<LetsCelebrate />} />
                    <Route path="/celebrate/:activityId" element={<ShareActivityRedirect />} />
                    <Route path="/api/share/activity/:activityId" element={<ShareActivityRedirect />} />
                    <Route path="/api/share/group/:groupId" element={<ShareGroupRedirect />} />
                    <Route path="/activity/:id/edit" element={<EditActivity />} />
                    <Route path="/activity/:id/pricing" element={<PricingWizard />} />
                    <Route path="/activity/:id/invite" element={<InviteParticipants />} />
                    <Route path="/activity/:id/join" element={<JoinActivity />} />
                    <Route path="/activity/:id/attend" element={<AttendeeSelection />} />
                    <Route path="/pay/:id" element={<DirectPaymentEntry />} />
                    <Route path="/activity/:id/payment" element={<PaymentCollection />} />
                    <Route path="/activity/:id/post-pay-profile" element={<PostPayGroupProfile />} />
                    <Route path="/activity/:id/payment-setup" element={<SetPaymentCollection />} />
                    <Route path="/activity/:id/pay-smart" element={<PaySmart />} />
                    <Route path="/activity/:id/expenses" element={<ExpenseTracking />} />
                    <Route path="/activity/:id/balance" element={<BalanceCalculation />} />
                    <Route path="/activity/:id/chat" element={<ChatRouteRedirect />} />
                    <Route path="/activity/:id" element={<ActivityDetail />} />
                    <Route path="/share-payment-screenshot" element={<SharePaymentScreenshot />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/my-payments" element={<MyPayments />} />
                    <Route path="/groups" element={<Groups />} />
                    <Route path="/join-group/:inviteKey" element={<JoinGroup />} />
                    <Route path="/groups/:groupId/profile-fields" element={<GroupProfileFieldsConfig />} />
                    <Route path="/group/:groupId/treasurer" element={<GroupTreasurerSetup />} />
                    <Route path="/pro-request/:requestId/review" element={<ProRequestApproval />} />
                    <Route path="/balance" element={<Balance />} />
                    <Route path="/offers" element={<ErrorBoundary><Offers /></ErrorBoundary>} />
                    <Route path="/admin/offers" element={<AdminOffers />} />
                    <Route path="/admin/payments" element={<AdminPayments />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/features" element={<AdminFeatures />} />
                    <Route path="/activity/:id/continue-setup" element={<ContinueSetup />} />
                    <Route path="/scan" element={<QRScanner />} />
                    <Route path="/groups/:groupId/manage-qr" element={<ManageQRCodes />} />
                    <Route path="/groups/:groupId/customize" element={<CustomizeGroup />} />
                    <Route path="/groups/:groupId/settings" element={<GroupSettings />} />
                    <Route path="/groups/:groupId/dashboard" element={<GroupDashboard />} />
                    <Route path="/groups/:groupId/collections" element={<GroupCollections />} />
                  </Routes>
                  </Suspense>
                </ErrorBoundary>
              </div>
          </div>
          {isAuthenticated && !shouldHideNav && (
            <Suspense fallback={null}>
              <BottomNav />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <Router>
          <ToastProvider>
            <Toast />
            <AutoUpdater>
              <AppContent />
            </AutoUpdater>
          </ToastProvider>
        </Router>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
}

export default App;

